import React, { useState, useMemo } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

const BreakevenAnalysisPanel = ({ roiData, loading }) => {
  const { currentLanguage, t } = useLanguage();
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [customFixedCosts, setCustomFixedCosts] = useState('');
  const [customVariableCostPerPilgrim, setCustomVariableCostPerPilgrim] = useState('');
  const [customRevenuePerPilgrim, setCustomRevenuePerPilgrim] = useState('');

  const selectedGroup = useMemo(() => {
    if (!selectedGroupId || !roiData?.groups) return null;
    return roiData?.groups?.find(g => g?.id === selectedGroupId);
  }, [selectedGroupId, roiData]);

  const breakevenAnalysis = useMemo(() => {
    if (!selectedGroup) return null;

    const pilgrims = parseInt(selectedGroup?.pilgrims || 0);
    const revenue = parseFloat(selectedGroup?.revenue || 0);
    const costs = parseFloat(selectedGroup?.costs || 0);

    // Calculate per-pilgrim metrics
    const revenuePerPilgrim = pilgrims > 0 ? revenue / pilgrims : parseFloat(customRevenuePerPilgrim || 0);
    
    // Estimate fixed vs variable costs (assume 40% fixed, 60% variable)
    const fixedCosts = parseFloat(customFixedCosts) || (costs * 0.4);
    const variableCosts = costs - fixedCosts;
    const variableCostPerPilgrim = parseFloat(customVariableCostPerPilgrim) || (pilgrims > 0 ? variableCosts / pilgrims : 0);

    // Breakeven calculation: Fixed Costs / (Revenue per unit - Variable Cost per unit)
    const contributionMargin = revenuePerPilgrim - variableCostPerPilgrim;
    const breakevenPilgrims = contributionMargin > 0 ? Math.ceil(fixedCosts / contributionMargin) : 0;
    const breakevenRevenue = breakevenPilgrims * revenuePerPilgrim;

    // Safety margin
    const safetyMargin = pilgrims > 0 ? ((pilgrims - breakevenPilgrims) / pilgrims) * 100 : 0;

    // Projection data for chart
    const projectionData = [];
    const maxPilgrims = Math.max(pilgrims * 1.5, breakevenPilgrims * 1.5, 50);
    for (let i = 0; i <= maxPilgrims; i += Math.ceil(maxPilgrims / 20)) {
      projectionData?.push({
        pilgrims: i,
        revenue: i * revenuePerPilgrim,
        totalCosts: fixedCosts + (i * variableCostPerPilgrim),
        profit: (i * revenuePerPilgrim) - (fixedCosts + (i * variableCostPerPilgrim))
      });
    }

    return {
      fixedCosts,
      variableCostPerPilgrim,
      revenuePerPilgrim,
      contributionMargin,
      breakevenPilgrims,
      breakevenRevenue,
      currentPilgrims: pilgrims,
      safetyMargin,
      projectionData
    };
  }, [selectedGroup, customFixedCosts, customVariableCostPerPilgrim, customRevenuePerPilgrim]);

  const formatCurrency = (amount) => {
    if (currentLanguage === 'ar') {
      const formattedNumber = new Intl.NumberFormat('ar-MA', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })?.format(amount);
      return `${formattedNumber} ${t('financial.currency')}`;
    } else {
      return new Intl.NumberFormat('fr-MA', {
        style: 'currency',
        currency: 'MAD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })?.format(amount);
    }
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-popover border border-border rounded-lg p-3 shadow-warm-md">
          <p className="text-sm font-medium text-foreground mb-2">
            {payload?.[0]?.payload?.pilgrims} {currentLanguage === 'fr' ? 'pèlerins' : 'حجاج'}
          </p>
          {payload?.map((entry, index) => (
            <p key={index} className="text-sm font-semibold data-text" style={{ color: entry?.color }}>
              {entry?.name}: {formatCurrency(entry?.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border shadow-warm p-6 animate-pulse">
        <div className="h-6 bg-muted rounded w-48 mb-6"></div>
        <div className="h-80 bg-muted rounded"></div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border shadow-warm p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">
          {currentLanguage === 'fr' ? 'Analyse du Seuil de Rentabilité' : 'تحليل نقطة التعادل'}
        </h3>
        <Icon name="TrendingUp" size={20} className="text-primary" />
      </div>
      {/* Group Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-foreground mb-2">
          {currentLanguage === 'fr' ? 'Sélectionner un groupe' : 'اختر مجموعة'}
        </label>
        <select
          value={selectedGroupId || ''}
          onChange={(e) => setSelectedGroupId(e?.target?.value)}
          className="w-full px-4 py-2 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="">
            {currentLanguage === 'fr' ? 'Choisir un groupe...' : 'اختر مجموعة...'}
          </option>
          {roiData?.groups?.map(group => (
            <option key={group?.id} value={group?.id}>
              {group?.name} ({group?.pilgrims} {currentLanguage === 'fr' ? 'pèlerins' : 'حجاج'})
            </option>
          ))}
        </select>
      </div>
      {selectedGroup && breakevenAnalysis && (
        <>
          {/* Custom Parameters */}
          <div className="bg-muted/50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-foreground mb-4">
              {currentLanguage === 'fr' ? 'Paramètres personnalisés (optionnel)' : 'معاملات مخصصة (اختياري)'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block caption text-muted-foreground mb-2">
                  {currentLanguage === 'fr' ? 'Coûts fixes (MAD)' : 'التكاليف الثابتة (درهم)'}
                </label>
                <Input
                  type="number"
                  value={customFixedCosts}
                  onChange={(e) => setCustomFixedCosts(e?.target?.value)}
                  placeholder={breakevenAnalysis?.fixedCosts?.toFixed(0)}
                />
              </div>
              <div>
                <label className="block caption text-muted-foreground mb-2">
                  {currentLanguage === 'fr' ? 'Coût variable/pèlerin (MAD)' : 'التكلفة المتغيرة/حاج (درهم)'}
                </label>
                <Input
                  type="number"
                  value={customVariableCostPerPilgrim}
                  onChange={(e) => setCustomVariableCostPerPilgrim(e?.target?.value)}
                  placeholder={breakevenAnalysis?.variableCostPerPilgrim?.toFixed(0)}
                />
              </div>
              <div>
                <label className="block caption text-muted-foreground mb-2">
                  {currentLanguage === 'fr' ? 'Revenu/pèlerin (MAD)' : 'الإيراد/حاج (درهم)'}
                </label>
                <Input
                  type="number"
                  value={customRevenuePerPilgrim}
                  onChange={(e) => setCustomRevenuePerPilgrim(e?.target?.value)}
                  placeholder={breakevenAnalysis?.revenuePerPilgrim?.toFixed(0)}
                />
              </div>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-primary/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Users" size={16} className="text-primary" />
                <p className="caption text-muted-foreground">
                  {currentLanguage === 'fr' ? 'Seuil de rentabilité' : 'نقطة التعادل'}
                </p>
              </div>
              <p className="text-2xl font-bold text-primary data-text">
                {breakevenAnalysis?.breakevenPilgrims}
              </p>
              <p className="caption text-muted-foreground">
                {currentLanguage === 'fr' ? 'pèlerins' : 'حجاج'}
              </p>
            </div>

            <div className="bg-success/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="DollarSign" size={16} className="text-success" />
                <p className="caption text-muted-foreground">
                  {currentLanguage === 'fr' ? 'Revenu au seuil' : 'الإيراد عند التعادل'}
                </p>
              </div>
              <p className="text-xl font-bold text-success data-text">
                {formatCurrency(breakevenAnalysis?.breakevenRevenue)}
              </p>
            </div>

            <div className="bg-blue-500/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="TrendingUp" size={16} className="text-blue-500" />
                <p className="caption text-muted-foreground">
                  {currentLanguage === 'fr' ? 'Marge de contribution' : 'هامش المساهمة'}
                </p>
              </div>
              <p className="text-xl font-bold text-blue-500 data-text">
                {formatCurrency(breakevenAnalysis?.contributionMargin)}
              </p>
              <p className="caption text-muted-foreground">
                {currentLanguage === 'fr' ? 'par pèlerin' : 'لكل حاج'}
              </p>
            </div>

            <div className={`rounded-lg p-4 ${
              breakevenAnalysis?.safetyMargin >= 20 ? 'bg-success/10' :
              breakevenAnalysis?.safetyMargin >= 10 ? 'bg-warning/10' : 'bg-error/10'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Shield" size={16} className={`${
                  breakevenAnalysis?.safetyMargin >= 20 ? 'text-success' :
                  breakevenAnalysis?.safetyMargin >= 10 ? 'text-warning' : 'text-error'
                }`} />
                <p className="caption text-muted-foreground">
                  {currentLanguage === 'fr' ? 'Marge de sécurité' : 'هامش الأمان'}
                </p>
              </div>
              <p className={`text-2xl font-bold data-text ${
                breakevenAnalysis?.safetyMargin >= 20 ? 'text-success' :
                breakevenAnalysis?.safetyMargin >= 10 ? 'text-warning' : 'text-error'
              }`}>
                {breakevenAnalysis?.safetyMargin?.toFixed(1)}%
              </p>
            </div>
          </div>

          {/* Breakeven Chart */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-muted-foreground mb-4">
              {currentLanguage === 'fr' ? 'Projection Revenus vs Coûts' : 'توقعات الإيرادات مقابل التكاليف'}
            </h4>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={breakevenAnalysis?.projectionData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="pilgrims" 
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                  label={{ 
                    value: currentLanguage === 'fr' ? 'Nombre de pèlerins' : 'عدد الحجاج', 
                    position: 'insideBottom', 
                    offset: -5 
                  }}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `${(value / 1000)?.toFixed(0)}k`}
                  label={{ 
                    value: currentLanguage === 'fr' ? 'Montant (MAD)' : 'المبلغ (درهم)', 
                    angle: -90, 
                    position: 'insideLeft' 
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine 
                  x={breakevenAnalysis?.breakevenPilgrims} 
                  stroke="#8b5cf6" 
                  strokeDasharray="5 5"
                  label={{ 
                    value: currentLanguage === 'fr' ? 'Seuil' : 'التعادل', 
                    fill: '#8b5cf6',
                    fontSize: 12
                  }}
                />
                <ReferenceLine 
                  x={breakevenAnalysis?.currentPilgrims} 
                  stroke="#3b82f6" 
                  strokeDasharray="3 3"
                  label={{ 
                    value: currentLanguage === 'fr' ? 'Actuel' : 'الحالي', 
                    fill: '#3b82f6',
                    fontSize: 12
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#10b981" 
                  strokeWidth={3}
                  dot={false}
                  name={currentLanguage === 'fr' ? 'Revenus' : 'الإيرادات'}
                />
                <Line 
                  type="monotone" 
                  dataKey="totalCosts" 
                  stroke="#ef4444" 
                  strokeWidth={3}
                  dot={false}
                  name={currentLanguage === 'fr' ? 'Coûts totaux' : 'إجمالي التكاليف'}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Insights */}
          <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-foreground mb-2">
                  {currentLanguage === 'fr' ? 'Insights' : 'رؤى'}
                </h4>
                <ul className="space-y-2 text-sm text-foreground">
                  <li>
                    {currentLanguage === 'fr' 
                      ? `Vous devez avoir au moins ${breakevenAnalysis?.breakevenPilgrims} pèlerins pour couvrir tous les coûts.`
                      : `يجب أن يكون لديك على الأقل ${breakevenAnalysis?.breakevenPilgrims} حاج لتغطية جميع التكاليف.`}
                  </li>
                  <li>
                    {currentLanguage === 'fr'
                      ? `Actuellement, vous avez ${breakevenAnalysis?.currentPilgrims} pèlerins, ce qui vous donne une marge de sécurité de ${breakevenAnalysis?.safetyMargin?.toFixed(1)}%.`
                      : `حاليًا، لديك ${breakevenAnalysis?.currentPilgrims} حاج، مما يمنحك هامش أمان ${breakevenAnalysis?.safetyMargin?.toFixed(1)}%.`}
                  </li>
                  <li>
                    {currentLanguage === 'fr'
                      ? `Chaque pèlerin supplémentaire au-delà du seuil génère ${formatCurrency(breakevenAnalysis?.contributionMargin)} de profit.`
                      : `كل حاج إضافي بعد نقطة التعادل يولد ${formatCurrency(breakevenAnalysis?.contributionMargin)} من الربح.`}
                  </li>
                  {breakevenAnalysis?.safetyMargin < 10 && (
                    <li className="text-error font-semibold">
                      {currentLanguage === 'fr' ?'⚠️ Attention: Marge de sécurité faible. Considérez augmenter le nombre de pèlerins ou réduire les coûts.' :'⚠️ تحذير: هامش أمان منخفض. فكر في زيادة عدد الحجاج أو تقليل التكاليف.'}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
      {!selectedGroup && (
        <div className="flex flex-col items-center justify-center h-64 bg-muted/50 rounded-lg">
          <Icon name="BarChart3" size={48} className="text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-center">
            {currentLanguage === 'fr' ?'Sélectionnez un groupe pour voir l\'analyse du seuil de rentabilité' :'اختر مجموعة لعرض تحليل نقطة التعادل'}
          </p>
        </div>
      )}
    </div>
  );
};

export default BreakevenAnalysisPanel;